import { ExtendedDriver } from '@hooks/useDriverTractors.types';
import Tag from '@components/Tag';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { isDriverSelectable } from './types';
import { ErrorOutlineTwoTone } from '@mui/icons-material';
import { Tooltip } from '@mui/material';

import styles from './DataGrid.Driver.module.css';

interface MobileDriverItemProps {
	driver: ExtendedDriver;
	applyDefaultTractorValidation: boolean;
	requiresHazmatCert: boolean;
}

/**
 * Mobile-friendly list of Drivers used for assigning a driver/truck. Displays Driver Name and Code along with Elog/Hazmat compliance.
 */
export default function MobileDriverItem({ driver, applyDefaultTractorValidation, requiresHazmatCert }: MobileDriverItemProps) {
	const selectionStatus = isDriverSelectable(driver, requiresHazmatCert, applyDefaultTractorValidation);
	const defaultTractor = driver.tractors.find((tractor) => tractor.id === driver.tractorId);

	return (
		<div className={styles['mobile-li']}>
			<div className={styles['mobile-li-header']}>
				<Tooltip
					slotProps={{
						popper: {
							className: styles.tooltip
						}
					}}
					title={
						selectionStatus.disabledReason.length > 0 && (
							<ul className={styles['disabled-reasons-ul']}>
								{selectionStatus.disabledReason.map((reason, index) => (
									<li key={`disabled-reason-${String(index)}`}>{reason}</li>
								))}
							</ul>
						)
					}
				>
					<div className={styles['driver-group']}>
						{!selectionStatus.isSelectable && <ErrorOutlineTwoTone className={styles['mobile-disabled']} />}
						<span>({driver.id})</span> -<span className={styles['driver-name']}>{driver.name}</span>
					</div>
				</Tooltip>

				<div className={styles['mobile-li-tags']}>
					{Boolean(driver.hazmatExpirationDate && driver.hazmatExpirationDate.unix >= dayjsNow().unix()) && (
						<Tag
							text='Hazmat'
							tagStyle='haz'
							height='96%'
							width='80px'
							fontSize='18px'
							dense
						/>
					)}

					{driver.isTankerEndorsed && (
						<Tag
							text='Tanker'
							tagStyle='bulk'
							height='96%'
							width='80px'
							fontSize='18px'
							dense
						/>
					)}

					{driver.hasELogs && (
						<Tag
							text='E-Logs'
							tagStyle='live'
							height='96%'
							width='80px'
							fontSize='18px'
							dense
						/>
					)}
				</div>
			</div>

			<div className={styles['mobile-li-info']}>
				<div className={styles['mobile-li-info-left']}>
					<div>
						<span className={styles['work-hrs-lbl']}>Truck (Plt #):</span>
						<span>
							{defaultTractor && driver.tractors.length === 1 && (
								<span className={styles['work-hrs-val']}>
									{defaultTractor.id}
									{defaultTractor.pltNo ? ` (${defaultTractor.pltNo})` : ''}
								</span>
							)}
							{driver.tractors.length > 1 && <>*</>}
						</span>
					</div>
					<div className={styles['work-hrs-group']}>
						<span className={styles['work-hrs-lbl']}>Drive:</span>
						<span className={styles['work-hrs-val']}>{driver.drvnHrs}</span>
						<span className={styles['work-hrs-lbl']}>Shift:</span>
						<span className={styles['work-hrs-val']}>{driver.shftHrs}</span>
					</div>
					<div className={styles['work-hrs-group']}>
						<span className={styles['work-hrs-lbl']}>Break:</span>
						<span className={styles['work-hrs-val']}>{driver.brkCntdwn}</span>
						<span className={styles['work-hrs-lbl']}>Cycle:</span>
						<span className={styles['work-hrs-val']}>{driver.cycleHrs}</span>
					</div>
				</div>
				<div className={styles['mobile-li-info-right']}>
					<div className={styles['work-hrs-group']}>
						<span className={styles['work-hrs-lbl']}>Hazmat Exp.:</span>
						<span className={styles['exp-val']}>{driver.hazmatExpirationDate?.shortDateExtended}</span>
					</div>
					<div className={styles['work-hrs-group']}>
						<span className={styles['work-hrs-lbl']}>CDL Exp.:</span>
						<span className={styles['exp-val']}>{driver.cdlExpirationDate?.shortDateExtended}</span>
					</div>
					<div className={styles['work-hrs-group']}>
						<span className={styles['work-hrs-lbl']}>Medical Exp.:</span>
						<span className={styles['exp-val']}>{driver.medExpirationDate?.shortDateExtended}</span>
					</div>
				</div>
			</div>
		</div>
	);
}
